import { TeamName } from "@/components/TeamName";
import {
  getAppointmentTitle,
  getIcon,
  getRenderTypeInfo,
} from "@/features/calendar/utils";
import {
  type ExtendedAppointment,
  useAppointmentFromOpportunity,
} from "@repo/api";
import { LightMode, ListView, ListViewItem, Text } from "@repo/skapa";
import { t } from "@repo/translation";
import { CoWorkerName, OneLineInfo, useCustomer, useSettings } from "@repo/ui";
import { isDefined } from "@repo/utils";

import { useActiveProject } from "../project/ActiveProject";
import { AppointmentActionButtons } from "./AppointmentActionButtons";
import { AppointmentStatusIndicator } from "./AppointmentStatusIndicator";
import { AppointmentTime } from "./modals/components/AppointmentTime";
import { isCancelledStatus, isCompletedStatus } from "./utils";

export const AppointmentInfo = ({
  appointment,
  variant,
  editable = true,
  onClickMoreActions,
  onClickStartEnd,
  mutateSearchAppointments,
}: {
  appointment: ExtendedAppointment;
  variant: "AGENDA" | "MANAGE";
  editable?: boolean;
  onClickMoreActions?: (...args: any[]) => any;
  onClickStartEnd?: (...args: any[]) => any;
  mutateSearchAppointments?: () => void;
}) => {
  const { task_configuration } = useSettings();
  const { opportunity } = useActiveProject();
  const { customer } = useCustomer(opportunity?.id);
  const shouldFetch = opportunity?.id ? false : true;
  const { data: allAppointmentDetails } = useAppointmentFromOpportunity(
    appointment,
    shouldFetch
  );
  appointment = allAppointmentDetails || appointment;

  const textColor =
    isCancelledStatus(appointment.appointmentStatus) ||
    isCompletedStatus(appointment.appointmentStatus)
      ? LightMode.colourTextAndIcon3
      : LightMode.colourTextAndIcon1;

  return (
    <>
      <ListView
        showDivider={false}
        id={`appointment-info-${appointment.orderNumber}`}
      >
        <ListViewItem
          control={
            !editable
              ? "default"
              : variant === "MANAGE"
                ? "navigational"
                : "default"
          }
          leadingIcon={getIcon(
            getRenderTypeInfo(appointment),
            undefined,
            task_configuration?.task_types
          )}
          title={
            <AppointmentTime
              variant={variant}
              status={appointment.appointmentStatus}
              startTime={appointment.timeWindowStartTime}
              endTime={appointment.timeWindowEndTime}
              customerTimezone={customer?.timezone}
            />
          }
          description={
            <div>
              <Text
                textStyle={variant === "AGENDA" ? "Heading.S" : undefined}
                lineThrough={isCancelledStatus(appointment.appointmentStatus)}
                color={textColor}
              >
                {getAppointmentTitle(appointment)}
              </Text>
              <OneLineInfo
                info={[
                  appointment.locationName,
                  appointment.assignedCoworker ? (
                    <CoWorkerName
                      email={appointment.assignedCoworker}
                      fragment
                    />
                  ) : appointment.assignedTeam ? (
                    <TeamName teamId={appointment.assignedTeam} fragment />
                  ) : (
                    t("label.unassigned")
                  ),
                  appointment.orderNumber !== undefined
                    ? String(appointment.orderNumber)
                    : undefined,
                ].filter(isDefined)}
              />
            </div>
          }
          addOn={
            <AppointmentStatusIndicator
              status={appointment.appointmentStatus}
            />
          }
        />
      </ListView>

      {editable && (
        <AppointmentActionButtons
          appointment={appointment}
          showMoreActions={variant === "MANAGE"}
          onClickMoreActions={onClickMoreActions}
          onClickStartEnd={onClickStartEnd}
          mutateSearchAppointments={mutateSearchAppointments}
        />
      )}
    </>
  );
};
